// Dos datos del onboarding que hasta ahora sólo vivían en el `localStorage` del
// navegador y por lo tanto no se podían mirar desde afuera
// (ADR-2026-08-07-onboarding-producto-y-descarga-en-supabase):
//
//   `company_profiles.onboarding_producto` → qué eligió en el paso 4: `'upload'`
//     (subió su propia foto) o el id del ejemplo, que depende del rubro elegido en el
//     paso 2 (`belleza_mascara`, `moda_cartera`, `gastro_pizza`, … y los dos genéricos
//     `cartera_verde` / `camisa_hombre` para los rubros sin set propio — ver
//     `getExampleProducts` en `accountSetup.ts`, que es la lista viva).
//     Se pisa en cada "Generar imagen": vale la ÚLTIMA elección, que es con la que
//     realmente terminó el alta.
//   `company_profiles.onboarding_descargo` → `true` si se llevó al menos una de las
//     primeras imágenes generadas.
//
// El `localStorage` NO se reemplaza: `atlas:setup_downloaded` sigue siendo lo que
// decide el upsell de planes al salir del alta (ADR-2026-07-29). Esto es medición,
// no comportamiento.
//
// TODO se hace best-effort y sin `await` desde las pantallas: un fallo acá es un dato
// de segmentación que falta, nunca un alta rota ni una descarga que no ocurre. Va
// directo por PostgREST (no por atlas-backend) porque la RLS "Editar perfil de la
// propia compania" ya permite que el usuario actualice su propia fila, que es el mismo
// camino que usan `tipos_contenido` y el rubro del alta.

import { supabase } from './supabase';

/**
 * `profiles.id_company` del usuario logueado, o `null`.
 *
 * Reintenta una vez: en el paso 4 la empresa se creó hace segundos (al terminar el
 * paso 3) y un blip de PostgREST no debería costarnos el dato. Si igual no se puede
 * resolver, se abandona en silencio.
 */
async function idCompanyDelUsuario(): Promise<string | null> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) return null;

  for (let intento = 1; intento <= 2; intento++) {
    const { data, error } = await supabase
      .from('profiles')
      .select('id_company')
      .eq('id', user.id)
      .single();
    if (!error) return (data as { id_company?: string | null } | null)?.id_company ?? null;
  }
  return null;
}

/** Update best-effort sobre la empresa del usuario. Nunca lanza. */
async function actualizarEmpresa(campos: Record<string, unknown>): Promise<void> {
  try {
    const idCompany = await idCompanyDelUsuario();
    if (!idCompany) return;
    await supabase.from('company_profiles').update(campos).eq('id_company', idCompany);
  } catch {
    /* best-effort: medición, no puede romper el flujo */
  }
}

/**
 * Paso 4: con qué se generó. `valor` es `'upload'` o el id del ejemplo elegido — el
 * mismo string que va a `SETUP_GENERATION_KEY`, así los dos lados no se pueden
 * desincronizar.
 *
 * Fire-and-forget: la pantalla navega a la generación sin esperar esto.
 */
export function marcarProductoDelOnboarding(valor: string): void {
  void actualizarEmpresa({ onboarding_producto: valor });
}

/**
 * Se llevó al menos una de las primeras imágenes. Lo llama la pantalla de resultados
 * SÓLO cuando la descarga salió bien de verdad (el botón puede fallar, y un intento
 * fallido no es una descarga).
 */
export function marcarDescargaDelOnboarding(): void {
  void actualizarEmpresa({ onboarding_descargo: true });
}
